/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { addEdge, removeElements } from "react-flow-renderer";
import Sider from "./components/sider";
import { Node } from './components/Node';
import { fitView } from "../../../../helpers/flow";
import { initializeData, sourcesColors, isSource, isTarget } from './helper';

export function useNodes({
  data,
  flow = 'LR',
  disabledIds = [],
  activeSources,
  supplyChainActors,
  informationSources,
  ownDataSources,
  t = (s) => s,
  type,
  projectData = {},
  setPopoverVisible = () => {},
}) {
  const [X, Y] = flow === 'LR' ? [250, -80] : [-200, 180];
  const [initElements, setInitElements] = useState();
  const [elements, setElements] = useState([]);
  const [instance, setInstance] = useState(null);

  const reset = () => {
    if (instance) {
      setTimeout(() => fitView(instance.fitView), 100);
    }
  };

  const items = useMemo(() => {
    if (data && Object.keys(data).length) {
      return initializeData(data);
    }
    return null;
  }, [data]);

  const allItems = useMemo(() => {
    if (items) {
      const all = [];
      items.forEach((i) => i.forEach((a) => {
        all.push(a);
      }));
      return all;
    }

    return null;
  }, [items]);

  const onElementsRemove = (elementsToRemove) =>
    setElements((els) => removeElements(elementsToRemove, els));

  const onConnect = (params) => setElements((els) => addEdge({
    ...params, type: 'buttonedge',
  }, els));

  const onLoad = (reactFlowInstance) => {
    setTimeout(() => fitView(reactFlowInstance.fitView), 100);
    setInstance(reactFlowInstance);
  };

  const setAllPositions = (nodes) => {
    const groupedByLevel = {};

    nodes.forEach((n) => {
      if (groupedByLevel[n.level]) {
        groupedByLevel[n.level].push(n);
      } else {
        groupedByLevel[n.level] = [n];
      }
    });

    const elements = [];
    Object.entries(groupedByLevel).forEach(([k, v]) => {
      let group = [];
      if (flow === 'LR') {
        const maxChildY = ((v.length + 1) * (Y / 2));
        const prevGroupedByLevel = groupedByLevel[k - 1];
        const parentX = prevGroupedByLevel ? prevGroupedByLevel[0].position.x : 0;
        v.forEach((el, i) => {
          group.push({
            ...el,
            position: {
              x: parentX + X,
              y: maxChildY - (Y * (i + 1)),
            },
          });
        });
        elements.push(...group);
        groupedByLevel[k] = group;
      } else {
        const maxChildX = ((v.length + 1) * (X / 2));
        const prevGroupedByLevel = groupedByLevel[k - 1];
        const parentY = prevGroupedByLevel ? prevGroupedByLevel[0].position.y : 0;
        v.forEach((el, i) => {
          group.push({
            ...el,
            position: {
              y: parentY + Y,
              x: maxChildX - (X * (i + 1)),
            },
          });
        });
        elements.push(...group);
        groupedByLevel[k] = group;
      }
    });

    return elements;
  };

  const initializeItems = (exclude = []) => {
    let nodes = [];
    const con = [];
    const data = items;

    data.forEach((items, i) => {
      items.forEach((item) => {
        if (!exclude.includes(item.datastakeId)) {
          return;
        }

        if (item.targets) {
          item.targets.forEach((target) => {
            if (allItems.find((i) => i.id === target)) {
              const last = con.find((c) => c.target === target && c.source === item.id);
              if (!last && !item.isNotConnected) {
                con.push({
                  source: item.id,
                  target: target,
                });
              }
            }
          })
        }

        if (!nodes.find((n) => n.id === item.id)) {
          nodes.push({
            id: item.id,
            sourcePosition: flow === 'LR' ? 'left' : 'bottom',
            position: { x: 0, y: 0 },
            type: 'node',
            config: {
              ...item,
              isSource: isSource(item.id, con),
              isTarget: isTarget(item.id, con),
            },
            level: i,
          });
        }
      });
    });

    let edges = [];
    con.forEach(({ source, target, isInfoSource, isOwnData, isSupplyChainActor }) => {
      edges.push({
        id: `${source}-${target}`,
        source,
        target,
        isInfoSource,
        type: 'edge',
        isOwnData,
        isSupplyChainActor
      });
    });

    nodes = setAllPositions(nodes);

    setInitElements([
      ...nodes,
      ...edges,
    ]);
  };

  const getStroke = (el) => {
    if (el.isOwnData) {
      return '#113366';
    }
    if (el.isSupplyChainActor) {
      return sourcesColors.supplyChainActors;
    }
    if (el.isInfoSource) {
      return sourcesColors.informationSources;
    }

    return '#113366';
  }

  const renderSider = (activeNode = null, close = () => { }) => (
    <Sider
      activeNode={activeNode}
      activeSources={activeSources}
      supplyChainActors={supplyChainActors}
      projectData={projectData}
      informationSources={informationSources}
      ownDataSources={ownDataSources}
      t={t}
      type={type}
      close={close}
    />
  )

  useEffect(() => {
    if (initElements) {
      let _elements = initElements;

      const _values = _elements.map((el) => {
        switch (el.type) {
          case 'edge':
            return {
              id: el.id,
              source: el.source,
              target: el.target,
              style: {
                stroke: getStroke(el),
                strokeWidth: 1,
              }
            };
          case 'node':
            return {
              id: el.id,
              data: {
                label: (
                  <Node
                    setPopoverVisible={setPopoverVisible}
                    renderPopOver={renderSider}
                    el={el}
                  />
                ),
              },
              className: 'node',
              position: el.position,
              sourcePosition: flow === 'LR' ? 'right' : 'bottom',
              targetPosition: flow === 'LR' ? 'left' : 'top',
              style: {
                padding: 0,
              },
            };
          default:
            return null;
        }
      });

      setElements(_values);
    }
  }, [initElements, disabledIds]);

  useEffect(() => {
    if (items) {
      initializeItems(disabledIds);
    }
  }, [items, flow, disabledIds]);

  useEffect(() => {
    if (instance) {
      setTimeout(() => fitView(instance.fitView), 100);
    }
  }, [flow, data]);

  return {
    elements,
    initElements,
    onElementsRemove,
    onConnect,
    onLoad,
    reset,
    disabledIds,
  };
}
